import { addWeeks, endOfWeek, startOfWeek } from 'date-fns';
import { capitalize, isEmpty } from 'lodash';

import { FlightServiceRow } from '@/components/modules/flight-service/row';
import { SchedulesNoResults } from '@/components/modules/schedules/no-results';
import { useCurrentDate } from '@/hooks/current-date-provider';
import { useTruncated } from '@/hooks/use-truncated';
import { DAYS_OF_WEEK } from '@/lib/constants';
import { FlightServiceModel } from '@/lib/models/flight-service/types';
import { tv } from '@/utils/styles';

export interface FlightServiceGroupProps {
  services: FlightServiceModel[];
  activeWeek: number;
  freightOnly: boolean;
  onRowClick: (service: FlightServiceModel) => void;
}

// Function to check if two date ranges overlap
const dateRangesOverlap = (range1: [Date, Date], range2: [Date, Date]): boolean => {
  const [startA, endA] = range1;
  const [startB, endB] = range2;

  return !(endA < startB || startA > endB);
};

const getDateRange = (weekIndex = 0, currentDate = new Date()): [Date, Date] => {
  const now = addWeeks(currentDate, weekIndex);
  const from = startOfWeek(now);
  const to = endOfWeek(now);

  return [from, to];
};

const shouldFilterVehicle = (freightOnly: boolean, isFreightAircraft = false): boolean => {
  // If toggle is not on
  if (!freightOnly) return true;
  // If toggle is on, and vehicle is freighter
  if (freightOnly && isFreightAircraft) {
    return true;
  }
  // else don't show vehicle
  return false;
};

// Function to filter items based on date range overlap
const filterItemsByParams = (
  services: FlightServiceModel[],
  activeWeek: number,
  freightOnly: boolean,
  currentDate: Date = new Date(),
): FlightServiceModel[] => {
  const activeDateRange = getDateRange(activeWeek, currentDate);

  return services.filter((service) => {
    const { effectiveRange, isFreightAircraft } = service;
    // Match date range
    const [start, end] = effectiveRange;
    const itemDateRange: [Date, Date] = [new Date(start), new Date(end)];
    return dateRangesOverlap(activeDateRange, itemDateRange) && shouldFilterVehicle(freightOnly, isFreightAircraft);
  });
};

const FlightServiceGroup = ({ services, activeWeek, freightOnly, onRowClick }: FlightServiceGroupProps) => {
  const currentDate = useCurrentDate();
  const handleRowClick = (service: FlightServiceModel) => {
    onRowClick(service);
  };

  const filteredServices = filterItemsByParams(services, activeWeek, freightOnly, currentDate);
  const { rows, shouldShowLoadMore, shouldShowAllRows, showAllRows } = useTruncated(filteredServices, 20);

  const { cell, daysOfWeek, base, table, loadMore, noResults } = styles();

  return (
    <div className={base()}>
      <table className={table()}>
        <thead>
          <tr>
            <th aria-label="empty" className={cell({ className: 'w-[4%]' })}>
              &nbsp;
            </th>
            <th className={cell({ className: 'w-[10%]' })}>Flight #</th>
            <th className={cell({ className: 'w-[5%]' })}>Departs</th>
            <th className={cell({ className: 'w-[5%]' })}>Arrives</th>
            <th className={cell({ className: 'w-[5%]' })}>Duration</th>
            <th className={cell({ className: 'w-[15%]' })}>Aircraft / Vehicle</th>
            <th className={cell({ className: 'w-[10%]' })}>Vehicle Type</th>
            <th className={cell({ className: 'w-[10%]' })}>Emissions</th>
            <th className={cell({ className: 'w-[25%]' })}>
              <ol className={daysOfWeek()}>
                {DAYS_OF_WEEK.map((d) => (
                  <li key={d}>{capitalize(d)}</li>
                ))}
              </ol>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((service) => (
            <FlightServiceRow key={service.id} flightService={service} onClick={() => handleRowClick(service)} />
          ))}
        </tbody>
      </table>
      {isEmpty(rows) && (
        <SchedulesNoResults
          className={noResults()}
          message="There are no matching services operating during this time period"
          secondaryMessage="Please try a different try changing the date range or removing the 'Freighters only' filter."
        />
      )}
      {shouldShowLoadMore && !shouldShowAllRows && (
        <button type="button" className={loadMore()} onClick={() => showAllRows()}>
          Showing {rows.length} of {filteredServices.length} flights - <u>Show all</u>
        </button>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'mx-4 mb-10 mt-2 pb-6',
    table: 'w-full border-separate border-spacing-x-0 border-spacing-y-1',
    cell: 'sticky top-10 z-2 bg-white py-1 pl-4 text-left text-sm font-normal text-grey-500 last:pr-4',
    daysOfWeek: 'grid w-full grid-cols-7 text-center',
    noResults: 'rounded-md border border-grey-200',
    loadMore:
      'border-1 w-full rounded-md border-dashed border-grey-200 bg-white py-2 text-center text-baseSm text-text-secondary hover:border-grey-400 hover:text-text-primary',
  },
});

export { FlightServiceGroup };
